// src/pages/Transactions.js
import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import Navbar from '../components/Navbar';
import BottomNav from '../components/BottomNav';
import API from '../utils/api';
import { useAuth } from '../contexts/AuthContext';

const Transactions = () => {
  const [transactions, setTransactions] = useState([]);
  const [filteredTransactions, setFilteredTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);               // change if you want more/less per page
  const [filters, setFilters] = useState({
    type: '',
    status: '',
    startDate: '',
    endDate: '',
  });

  const navigate = useNavigate();
  const { isLoggedIn } = useAuth();

  /* --------------------------------------------------------------
     1. Fetch raw transactions (once, on mount or login change)
     -------------------------------------------------------------- */
  useEffect(() => {
    if (!isLoggedIn) {
      navigate('/login');
      return;
    }

    const fetchTransactions = async () => {
      setLoading(true);
      try {
        const response = await API.get('/user/dashboard'); // or a dedicated endpoint
        const txs = response.data.user?.transactions || [];
        setTransactions(txs);
        // initial filter (empty) – keeps UI in sync
        applyFilters(txs, filters);
      } catch (error) {
        toast.error('Failed to load transactions!');
        if (error.response?.status === 401) navigate('/login');
      } finally {
        setLoading(false);
      }
    };

    fetchTransactions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoggedIn, navigate]);   // ← only these two change the fetch

  /* --------------------------------------------------------------
     2. Apply filters – memoised so we can safely depend on it
     -------------------------------------------------------------- */
  const applyFilters = useCallback(
    (txs, currentFilters) => {
      let filtered = [...txs];

      if (currentFilters.type) {
        filtered = filtered.filter(
          (tx) => tx.type.toLowerCase() === currentFilters.type.toLowerCase()
        );
      }
      if (currentFilters.status) {
        filtered = filtered.filter(
          (tx) => tx.status.toLowerCase() === currentFilters.status.toLowerCase()
        );
      }
      if (currentFilters.startDate) {
        filtered = filtered.filter(
          (tx) => new Date(tx.date) >= new Date(currentFilters.startDate)
        );
      }
      if (currentFilters.endDate) {
        filtered = filtered.filter(
          (tx) => new Date(tx.date) <= new Date(currentFilters.endDate)
        );
      }

      // newest first
      filtered.sort((a, b) => new Date(b.date) - new Date(a.date));

      setFilteredTransactions(filtered);
      setCurrentPage(1); // reset pagination when filters change
    },
    [] // no external deps – the function is stable
  );

  /* --------------------------------------------------------------
     3. Re-apply filters whenever the user changes a filter
     -------------------------------------------------------------- */
  useEffect(() => {
    applyFilters(transactions, filters);
  }, [transactions, filters, applyFilters]);

  /* --------------------------------------------------------------
     4. UI handlers
     -------------------------------------------------------------- */
  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  // ---- pagination ----
  const indexOfLastTx = currentPage * itemsPerPage;
  const indexOfFirstTx = indexOfLastTx - itemsPerPage;
  const currentTxs = filteredTransactions.slice(indexOfFirstTx, indexOfLastTx);
  const totalPages = Math.ceil(filteredTransactions.length / itemsPerPage);

  const paginate = (page) => setCurrentPage(page);

  /* --------------------------------------------------------------
     5. Render
     -------------------------------------------------------------- */
  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <p>Loading transactions…</p>
      </div>
    );
  }

  const uniqueTypes = [...new Set(transactions.map((t) => t.type))];
  const uniqueStatuses = [...new Set(transactions.map((t) => t.status))];

  return (
    <div className="min-h-screen bg-white pt-20">
      <Navbar />
      <div className="container mx-auto px-4 py-8">
        <h1 className="text-2xl font-bold text-primary mb-6">
          Transaction History
        </h1>

        {/* ---------- FILTERS ---------- */}
        <div className="bg-white rounded-xl shadow-md border border-gray-200 mb-6 p-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Type
              </label>
              <select
                name="type"
                value={filters.type}
                onChange={handleFilterChange}
                className="w-full px-3 py-2 border rounded-md"
              >
                <option value="">All</option>
                {uniqueTypes.map((t) => (
                  <option key={t} value={t}>
                    {t.charAt(0).toUpperCase() + t.slice(1)}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Status
              </label>
              <select
                name="status"
                value={filters.status}
                onChange={handleFilterChange}
                className="w-full px-3 py-2 border rounded-md"
              >
                <option value="">All</option>
                {uniqueStatuses.map((s) => (
                  <option key={s} value={s}>
                    {s.charAt(0).toUpperCase() + s.slice(1)}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                From Date
              </label>
              <input
                type="date"
                name="startDate"
                value={filters.startDate}
                onChange={handleFilterChange}
                className="w-full px-3 py-2 border rounded-md"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                To Date
              </label>
              <input
                type="date"
                name="endDate"
                value={filters.endDate}
                onChange={handleFilterChange}
                className="w-full px-3 py-2 border rounded-md"
              />
            </div>
          </div>
        </div>

        {/* ---------- TABLE + PAGINATION ---------- */}
        {filteredTransactions.length === 0 ? (
          <p className="text-gray-500 text-center">
            No transactions match the current filters.
          </p>
        ) : (
          <div className="bg-white rounded-xl shadow-md border border-gray-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full min-w-max">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Type
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Amount
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Currency
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Date
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {currentTxs.map((tx, idx) => (
                    <tr
                      key={idx}
                      className="hover:bg-gray-50 transition-colors"
                    >
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-medium">
                        {tx.type.charAt(0).toUpperCase() + tx.type.slice(1)}
                      </td>
                      <td
                        className={`px-6 py-4 whitespace-nowrap text-sm font-medium ${
                          tx.amount > 0 ? 'text-success' : 'text-error'
                        }`}
                      >
                        {tx.amount > 0 ? '+' : '-'}$
                        {Math.abs(tx.amount).toLocaleString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {tx.currency.toUpperCase()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span
                          className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${
                            tx.status === 'completed'
                              ? 'bg-success/10 text-success'
                              : tx.status === 'pending'
                              ? 'bg-warning/10 text-warning'
                              : 'bg-error/10 text-error'
                          }`}
                        >
                          {tx.status.charAt(0).toUpperCase() +
                            tx.status.slice(1)}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {new Date(tx.date).toLocaleDateString('en-US', {
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit',
                        })}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* ---- Pagination Controls ---- */}
            <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200">
              <button
                onClick={() => paginate(currentPage - 1)}
                disabled={currentPage === 1}
                className="text-sm text-primary hover:text-primary-dark disabled:text-gray-400 font-medium transition-colors"
              >
                Previous
              </button>

              <span className="text-sm text-gray-700">
                Page {currentPage} of {totalPages}
              </span>

              <button
                onClick={() => paginate(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="text-sm text-primary hover:text-primary-dark disabled:text-gray-400 font-medium transition-colors"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
      <BottomNav />
    </div>
  );
};

export default Transactions;